import { Action, Reducer } from 'redux';

import { ReservationDTO, TableAvailabilityDTO } from 'api/controllers/reservations';
import { format } from 'date-fns';

import { DATE_FORMAT, isOnLoadedDay } from 'helpers/reservationHelper';
import { ReservationsAction } from './actions';

export interface ReservationsState {
    requests: ReservationDTO[];
    requestsRestaurantId: string | null;
    selectedRequestId: string | null;
    active: {
        day: string | null | undefined;
        reservations: ReservationDTO[];
    };
    selectedActiveId: string | null;
    availability: {
        key: string | null;
        tables: TableAvailabilityDTO[];
    };
}

export const initialState: ReservationsState = {
    requests: [],
    requestsRestaurantId: null,
    selectedRequestId: null,
    active: { day: undefined, reservations: [] },
    selectedActiveId: null,
    availability: { key: null, tables: [] },
};

function withoutRequest(state: ReservationsState, id: string): ReservationsState {
    return {
        ...state,
        requests: state.requests.filter((request) => request.id !== id),
        selectedRequestId: state.selectedRequestId === id ? null : state.selectedRequestId,
    };
}

export const reducer: Reducer<ReservationsState> = (
    state: ReservationsState | undefined,
    incomingAction: Action
): ReservationsState => {
    if (state === undefined) {
        return initialState;
    }

    const action = incomingAction as ReservationsAction;

    switch (action.type) {
        case 'RESERVATION_REQUESTS_SET':
            return {
                ...state,
                requests: action.requests,
                requestsRestaurantId: action.restaurantId,
                selectedRequestId: action.requests.some(
                    (request) => request.id === state.selectedRequestId
                )
                    ? state.selectedRequestId
                    : null,
            };
        case 'RESERVATION_REQUEST_SELECT':
            return { ...state, selectedRequestId: action.id };
        case 'RESERVATION_REQUEST_SAVED':
            return {
                ...state,
                requests: state.requests.map((request) =>
                    request.id === action.reservation.id ? action.reservation : request
                ),
            };
        case 'RESERVATION_REQUEST_REMOVED':
            return withoutRequest(state, action.id);
        case 'RESERVATION_CONFIRMED': {
            const { reservation } = action;
            const next = withoutRequest(state, reservation.id);
            return {
                ...next,
                selectedActiveId: reservation.id,
                active: isOnLoadedDay(
                    reservation.reservationStartDateTime,
                    next.active.day,
                    format(new Date(), DATE_FORMAT)
                )
                    ? {
                          ...next.active,
                          reservations: next.active.reservations
                              .filter((item) => item.id !== reservation.id)
                              .concat(reservation),
                      }
                    : next.active,
            };
        }
        case 'RESERVATION_REALTIME_UPDATE': {
            const { reservation } = action;
            let next = state;
            if (state.requestsRestaurantId === reservation.restaurantId) {
                if (reservation.status === 'Requested' && !reservation.isArchived) {
                    const exists = state.requests.some((item) => item.id === reservation.id);
                    next = {
                        ...next,
                        requests: exists
                            ? state.requests.map((item) =>
                                  item.id === reservation.id ? reservation : item
                              )
                            : state.requests.concat(reservation),
                    };
                } else {
                    next = withoutRequest(state, reservation.id);
                }
            }

            const isActive =
                !reservation.isArchived &&
                (reservation.status === 'Confirmed' || reservation.status === 'Seated');
            const matchesLoadedDay = isOnLoadedDay(
                reservation.reservationStartDateTime,
                next.active.day,
                format(new Date(), DATE_FORMAT)
            );
            const isLoaded = next.active.reservations.some((item) => item.id === reservation.id);
            if (isLoaded || (isActive && matchesLoadedDay)) {
                const reservations = next.active.reservations.filter(
                    (item) => item.id !== reservation.id
                );
                if (isActive && matchesLoadedDay) {
                    reservations.push(reservation);
                }
                next = {
                    ...next,
                    active: { ...next.active, reservations },
                    selectedActiveId:
                        next.selectedActiveId === reservation.id &&
                        !reservations.some((item) => item.id === reservation.id)
                            ? null
                            : next.selectedActiveId,
                };
            }
            return next;
        }
        case 'RESERVATION_ACTIVE_LOAD':
            return action.day === state.active.day
                ? state
                : { ...state, active: { day: action.day, reservations: [] } };
        case 'RESERVATION_ACTIVE_SET':
            return action.day === state.active.day
                ? { ...state, active: { day: action.day, reservations: action.reservations } }
                : state;
        case 'RESERVATION_ACTIVE_SELECT':
            return { ...state, selectedActiveId: action.id };
        case 'RESERVATION_AVAILABILITY_LOAD':
            return action.key === state.availability.key
                ? state
                : { ...state, availability: { key: action.key, tables: [] } };
        case 'RESERVATION_AVAILABILITY_SET':
            return action.key === state.availability.key
                ? { ...state, availability: { key: action.key, tables: action.tables } }
                : state;
        case 'RESERVATION_AVAILABILITY_CLEAR':
            return { ...state, availability: initialState.availability };
        default:
            return state;
    }
};
